import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useContext, useState, useEffect, useCallback } from 'react';

import dt from '~/testing';
import { FEATURE } from '~/common/enums/feature.enum';
import { KYCContext } from '~/components/providers/KYCProvider';
import { deleteSessionStorage } from '~/utils/storage';
import { Routes } from '~/domains/common/constants/routes';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';
import { parsePromoTicketFromDepositBonus } from '~/domains/promotions/utils/parsePromoTickets';
import useProtectedGuard from '~/domains/protectionGuards/hooks/useProtectedGuard';
import { ProtectionGuardType } from '~/domains/protectionGuards/types';
import { PromotionActionLocation } from '~/domains/promotions/constants/analytics-enums';
import { useOpenPromoTermsModal } from '~/domains/promotions/hooks/useOpenPromoTermsModal';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';
import { SocureKycStatus } from '~/services/users/types';
import type { BonusDefinition, NormalizedBonus } from '~/domains/promotions';
import { denormalizeBonus } from '~/domains/promotions/utils/formatters';

import { useDepositBonusSelection } from '../hooks/useDepositBonusSelection';
import { useDepositContinueHandler } from '../hooks/useDepositContinueHandler';

interface UseDepositViewProps {
  contestIdForPostDeposit: string;
  isRedirectOnInvalidAccess: boolean;
  redirectToFromProps: string;
  selectedBonusId: string;
}

function useDepositView({
  contestIdForPostDeposit,
  isRedirectOnInvalidAccess,
  redirectToFromProps,
  selectedBonusId,
}: UseDepositViewProps) {
  const { t } = useTranslation('payments');
  const { t: tPromotions } = useTranslation('promotions');
  const { KYCStatus } = useContext(KYCContext);
  const { closeModal, openModalUnique } = useContext(RootModalsContext);
  const { openModalUnique: openPromoModalUnique, closeModal: closePromoModal } = useContext(PromotionsModalsContext);

  const router = useRouter();
  const isPageContext = router.pathname === Routes.deposit();
  // Merge redirectTo from props (modal) and query params (page/URL)
  // Props take precedence over query params
  const redirectToFromQuery = router.query.redirectTo as string;
  const redirectTo = redirectToFromProps ?? redirectToFromQuery;
  const previousRoute = (router.query.redirectTo as string) || Routes.home;

  // Clear any existing deposit success flag when starting a new deposit flow
  // This handles both direct navigation (router.push) and page refresh scenarios
  useEffect(() => {
    deleteSessionStorage('deposit_success');
  }, []);

  // State for in-place deposit success (for direct deposits on page context)
  const [completedDepositAmount, setCompletedDepositAmount] = useState<number | null>(null);
  const [completedDepositResponse, setCompletedDepositResponse] = useState<
    IPostWalletsDepositsResponse | undefined
  >(undefined);

  // Feature flags
  const isDepositEnabled = useIsFeatureEnabled(FEATURE.ENABLE_CONTEST_DETAILS_JOIN_AND_DEPOSIT_BUTTONS);
  const isDepositMatchBonusEnabled = useIsFeatureEnabled(FEATURE.ENABLE_DEPOSIT_MATCH_BONUS);

  // Hook 1: Bonus selection
  const { selectedBonus } = useDepositBonusSelection({
    selectedBonusId,
    isDepositMatchBonusEnabled,
  });

  // Continue handler for in-place success (direct deposits on page context)
  const handleOnContinue = useDepositContinueHandler({
    redirectTo,
    contestId: contestIdForPostDeposit,
  });

  // Hook 3: Show promo bonus modal if the selected bonus has a trigger (means was auto-applied)
  useEffect(() => {
    if (selectedBonus && selectedBonus.trigger && KYCStatus === SocureKycStatus.ACCEPT) {
      const bonusModalProps = {
        promoCode: selectedBonus.id,
        title: tPromotions('bonusModal.titleDepositBonus'),
        description: tPromotions('bonusModal.descriptionDepositBonus'),
        bonuses: {
          promotions: parsePromoTicketFromDepositBonus(selectedBonus, tPromotions),
          expiresAt: selectedBonus.assignmentExpiresAt,
        },
        actionButtonLabel: tPromotions('bonusModal.actionAcknowledge'),
        actionButtonTestId: dt.payments.deposits.bonusModal.actionButton,
      }

      openPromoModalUnique({
        type: 'PromoBonusModal' as const,
        ...bonusModalProps,
        onAction: () => {
          closePromoModal('PromoBonusModal');
        },
      });
    }
  }, [selectedBonus]);

  // Deposit completion handler
  const handleSetCompletedDepositAmount = (
    amount: number,
    response?: IPostWalletsDepositsResponse
  ) => {
    // Check if we're on the deposit page route (page context) vs modal context
    if (isPageContext) {
      // Page context: Show success in-place using React state (for direct deposits)
      // This matches the main branch pattern but uses the updated DepositCompleted component
      window.scrollTo(0, 0);
      setCompletedDepositAmount(amount);
      setCompletedDepositResponse(response);
    } else {
      // Modal context: Continue using existing modal behavior
      closeModal('DepositModal');
      openModalUnique({
        type: 'DepositCompletedModal',
        depositAmount: amount,
        depositResponse: response,
        redirectTo,
        contestId: contestIdForPostDeposit, // Pass contestId so modal knows to just close (not navigate) to allow EnterContestModal to reappear
      });
    }
  };

  // Protection guard
  const { isLoading: isProtectionLoading, isAccessible } = useProtectedGuard({
    action: 'make a deposit',
    protectionLevels: [
      ProtectionGuardType.KYC,
      ProtectionGuardType.LOCATION,
      ProtectionGuardType.GEOCOMPLY,
    ],
    checkOnMount: true,
    redirectOnClose: isRedirectOnInvalidAccess ? previousRoute : undefined,
    onAccessFailed: () => {
      closeModal('DepositModal');
    },
  });

  const { viewDepositBonusDetails } = useOpenPromoTermsModal({
    location: PromotionActionLocation.DEPOSIT,
  });

  // Deposit Modals are still using the normalized bonus,
  // so we need to denormalize it before passing to the modal
  const handleViewDepositBonusDetails = useCallback(
    (bonus: NormalizedBonus) => viewDepositBonusDetails(denormalizeBonus(bonus)),
    [viewDepositBonusDetails],
  );

  return {
    completedDepositAmount,
    completedDepositResponse,
    handleOnContinue,
    handleSetCompletedDepositAmount,
    isAccessible,
    isDepositEnabled,
    isDepositMatchBonusEnabled,
    isProtectionLoading,
    onDetailsClick: handleViewDepositBonusDetails,
    redirectTo,
    selectedBonus,
    t,
  };
}

export default useDepositView;
